import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { PartyPopper, Calendar, Users, UtensilsCrossed } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const empty = { name: "", email: "", phone: "", event_date: "", guest_count: "", event_type: "", details: "" };

export default function Catering() {
  const { t, language } = useLanguage();
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const types = t("catering.types");

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.details) {
      toast.error(t("catering.errorTitle"), { description: t("catering.errorDesc") });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        event_date: form.event_date || null,
        guest_count: form.guest_count ? Number(form.guest_count) : null,
        event_type: form.event_type || null,
        details: form.details,
        language,
      };
      await axios.post(`${API}/catering`, payload);
      toast.success(t("catering.successTitle"), { description: t("catering.successDesc") });
      setForm(empty);
    } catch {
      toast.error(t("catering.errorTitle"), { description: t("catering.errorDesc") });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="catering" data-testid="catering-section" className="relative bg-[#F9F6F0] py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <span className="text-[12px] uppercase tracking-[0.22em] text-[#C05A3A] font-medium inline-flex items-center gap-2">
            <PartyPopper className="h-4 w-4" strokeWidth={1.5} />
            {t("catering.eyebrow")}
          </span>
          <h2
            data-testid="catering-title"
            className="font-serif-display italic text-[#232321] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-4"
          >
            {t("catering.title")}
          </h2>
          <p className="mt-5 text-[#5C5C59] text-base leading-relaxed max-w-md">
            {t("catering.subtitle")}
          </p>

          <ul className="mt-8 space-y-3 text-sm text-[#5C5C59]">
            <li className="flex items-center gap-3">
              <Calendar className="h-4 w-4 text-[#C05A3A]" strokeWidth={1.5} />
              <span>{t("catering.note")}</span>
            </li>
            <li className="flex items-center gap-3">
              <Users className="h-4 w-4 text-[#C05A3A]" strokeWidth={1.5} />
              <span>10 – 500+ guests</span>
            </li>
            <li className="flex items-center gap-3">
              <UtensilsCrossed className="h-4 w-4 text-[#C05A3A]" strokeWidth={1.5} />
              <span>Carnitas · Tamales · Pozole · Taquizas</span>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="catering-form"
            className="bg-[#EADCCF]/60 border border-[#E2D9CD] rounded-2xl p-7 md:p-9 grid grid-cols-1 sm:grid-cols-2 gap-5"
          >
            <h3 className="sm:col-span-2 font-serif-display text-2xl md:text-3xl text-[#232321]">
              {t("catering.formTitle")}
            </h3>
            <Field label={t("catering.name")} testid="catering-name-input" name="name" value={form.name} onChange={onChange} required />
            <Field label={t("catering.email")} testid="catering-email-input" name="email" type="email" value={form.email} onChange={onChange} required />
            <Field label={t("catering.phone")} testid="catering-phone-input" name="phone" value={form.phone} onChange={onChange} required />
            <Field label={t("catering.date")} testid="catering-date-input" name="event_date" type="date" value={form.event_date} onChange={onChange} />
            <Field label={t("catering.guests")} testid="catering-guests-input" name="guest_count" type="number" min="1" value={form.guest_count} onChange={onChange} />
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-[#5C5C59]">
                {t("catering.eventType")}
              </label>
              <select
                data-testid="catering-type-input"
                name="event_type"
                value={form.event_type}
                onChange={onChange}
                className="mt-2 w-full bg-transparent border-b border-[#C05A3A]/40 focus:border-[#C05A3A] outline-none py-2 text-[#232321]"
              >
                <option value="">—</option>
                {types.map((tp) => (
                  <option key={tp} value={tp}>{tp}</option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-2">
              <label className="text-[11px] uppercase tracking-[0.22em] text-[#5C5C59]">
                {t("catering.details")}
              </label>
              <textarea
                data-testid="catering-details-input"
                name="details"
                rows={4}
                value={form.details}
                onChange={onChange}
                required
                className="mt-2 w-full bg-transparent border-b border-[#C05A3A]/40 focus:border-[#C05A3A] outline-none py-2 text-[#232321] resize-none"
              />
            </div>

            <div className="sm:col-span-2 flex justify-end">
              <button
                data-testid="catering-submit-btn"
                type="submit"
                disabled={submitting}
                className="rounded-full bg-[#C05A3A] hover:bg-[#A54C30] disabled:opacity-60 text-[#F9F6F0] px-7 py-3 text-[13px] font-semibold tracking-wide transition-all hover:-translate-y-0.5"
              >
                {submitting ? t("catering.sending") : t("catering.submit")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.22em] text-[#5C5C59]">{label}</label>
      <input
        data-testid={testid}
        {...props}
        className="mt-2 w-full bg-transparent border-b border-[#C05A3A]/40 focus:border-[#C05A3A] outline-none py-2 text-[#232321]"
      />
    </div>
  );
}
